/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import AppInput from "../../forms/AppInput";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { updateTutorSession } from "../../../api/endpoints/tutors/sessions";
import moment from "moment-timezone";
import DropDown from "../../forms/dropdown/DropDown";
import SessionModel from "../../../api/models/SessionModel";
import ExistConfirmationModal from "../ExistSessionModal";

type EndSessionProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  session: SessionModel;
  onEndSession?: () => void;
};
export default function EndSession(props: EndSessionProps) {
  const { open, setOpen, session, onEndSession } = props;
  const LocalBasePath = "page.tutor.dashboard.room";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const [errorEndingSession, setErrorEndingSession] = useState("");
  const SESSION_STATUS_OPTIONS = [
    {
      key: t("completed"),
      value: "completed",
    },
    {
      key: t("canceled"),
      value: "canceled",
    },
    {
      key: t("absence"),
      value: "absence",
    },
    {
      key: t("disconnected"),
      value: "disconnected",
    },
  ];

  const isSessionCompletedOrDisconnected = (status?: string) => {
    return ["completed", "disconnected"].includes(status!);
  };
  const [sessionData, setSessionData] = useState<any>({});
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const increment = (num: number) => num + 1;

  const endSession = async (values: any) => {
    setErrorEndingSession("");
    let newStartPage: number = session.student?.lastQuranPageReached || 0;
    if (isSessionCompletedOrDisconnected(values.status))
      newStartPage = increment(newStartPage);

    const upDateTutorSession = await updateTutorSession(session.id, {
      end_time: moment(),
      start_page_number: newStartPage,
      end_page_number: +values.pageTo,
      reported_status: values.status,
      tardiness: Number(values.tardiness) * 60,
    });
    if (upDateTutorSession.statusCode == 400) {
      setErrorEndingSession(upDateTutorSession.errors.end_page_number);
    } else {
      if (upDateTutorSession.isSuccess) {
        setOpen(false);
      }
      if (!!onEndSession) onEndSession();
    }

   
  };

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();
  const onSubmit = (data: any) => {
    setSessionData(data);
    setConfirmOpen(true);
  };
  const confirmEndSession = (confirm: boolean) => {
    if (confirm) {
      endSession(sessionData);
      setConfirmOpen(false);
    } else {
      setConfirmOpen(false);
    }
  };
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden"
        onClose={setOpen}
      >
        <div className="absolute inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div
            data-cy="endSessionModal"
            className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10"
          >
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="pointer-events-auto relative w-screen max-w-2xl">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-500"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-500"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 left-0 -ml-8 flex pt-4 pr-2 sm:-ml-10 sm:pr-4">
                    <button
                      type="button"
                      className="rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                      onClick={() => setOpen(false)}
                    >
                      <span className="sr-only">Close panel</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                  <div className="px-4 sm:px-6">
                    <Dialog.Title className="text-lg font-medium text-gray-900">
                      {" "}
                      {t("endSession")}
                    </Dialog.Title>
                  </div>

                  <div className="relative mt-6 flex-1 px-4 sm:px-6">
                    {/* Replace with your content */}
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="mt-1">
                        <DropDown
                          id="status"
                          name="status"
                          className="shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          schema={{ required: true }}
                          register={register}
                          label={t("status")}
                          placeholder={t("chooseStatus")}
                          error={!!errors.status}
                          options={SESSION_STATUS_OPTIONS}
                          cypressSelector={"status"}
                          defaultValue={getValues("status")}
                          setValue={(value: any) => {
                            setValue("status", value, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                        />
                      </div>
                      <div className="mt-6">
                        <div className="flex justify-between">
                          <AppInput
                            label={t("readPages")}
                            register={register}
                            error={errors.pageTo}
                            cypressSelector={"pageTo"}
                            scehema={{
                              validate: (val: any) => {
                                return (
                                  Number(val) >= 0 ||
                                  i18n.t("errors.input.negativeValue")
                                );
                              },
                              required: ["completed", "disconnected"].includes(
                                getValues("status")
                              ),
                            }}
                            placeholder={t("pageTo")}
                            type="number"
                            name="pageTo"
                            id="pageTo"
                            className="shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        {errorEndingSession && (
                          <p className="text-red-500 text-sm">{t("theReachedPageShouldBeBiggerThanTheStartedPage")}</p>
                        )}
                      </div>
                      <div className="mt-6">
                        <div className="flex justify-between">
                          <AppInput
                            label={t("sessionLatency")}
                            register={register}
                            error={errors.tardiness}
                            scehema={{
                              validate: (val: any) => {
                                return (
                                  Number(val) >= 0 ||
                                  i18n.t("errors.input.negativeValue")
                                );
                              },
                              required: ["completed", "disconnected"].includes(
                                getValues("status")
                              ),
                            }}
                            placeholder={t("tardinessTime")}
                            type="number"
                            name="tardiness"
                            id="tardiness"
                            descriptiveLabel={t("minutes")}
                            className="shadow-sm focus:ring-brandblue-500 focus:border-brandblue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <button
                        data-cy={"endSession"}
                        className="my-4 inline-flex  focus:ring-red-500 justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2  disabled:bg-gray-400"
                      >
                        {t("endSession")}
                      </button>
                    </form>
                    {/* /End replace */}
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
          <ExistConfirmationModal
            isOpen={confirmOpen}
            onClose={confirmEndSession}
          />
        </div>
      </Dialog>
    </Transition.Root>
  );
}
